import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CreateProfessorDto,
  Professor,
  ProfessorAssignment,
  UpdateProfessorDto,
} from '../interfaces/professor';
import {
  ApiCollectionResponse,
  ApiResponse,
  ApiSimpleResponse,
  ApiSingleResponse,
} from '../interfaces/responses/api-response';
import { HandleErrorsService } from './utils/handle-errors.service';
import { SessionResultMapperService } from './utils/session-result-mapper.service';

// ── Professor session DTOs ────────────────────────────────────────────────────

/** Raw shape returned by the API (nested objects) */
interface RawSessionDto {
  id: number;
  student?: { id?: string; firstName?: string; lastName?: string; email?: string };
  studentId?: string;
  studentFullName?: string;
  studentEmail?: string;
  test?: {
    id?: number; title?: string; testMode?: string; mode?: string;
    subjectId?: number; subjectName?: string;
    subject?: { id?: number; name?: string };
    learningPathId?: number; learningPathTitle?: string;
    professorId?: number; professorName?: string;
  };
  testId?: number;
  testTitle?: string;
  testMode?: string;
  subject?: { id?: number; name?: string };
  subjectName?: string;
  learningPathTitle?: string;
  status?: string;
  startedAt?: string;
  completedAt?: string | null;
  timeSpentSeconds?: number | null;
  attemptNumber?: number;
  score?: number | null;
  percentage?: number | null;
  percentageScore?: number | null;
  passed?: boolean | null;
  correctAnswers?: number | null;
  wrongAnswers?: number | null;
  unansweredQuestions?: number | null;
  totalQuestions?: number | null;
  totalPoints?: number | null;
  earnedPoints?: number | null;
  // Cheating stats — may arrive flat OR nested inside metadata/sessionMetadata
  tabSwitchCount?: number;
  focusLossCount?: number;
  fullscreenExitCount?: number;
  pageVisibilityChanges?: number;
  metadata?: {
    tabSwitchCount?: number;
    tabSwitches?: number;
    focusLossCount?: number;
    focusLosses?: number;
    fullscreenExitCount?: number;
    fullscreenExits?: number;
    pageVisibilityChanges?: number;
  };
  sessionMetadata?: {
    tabSwitchCount?: number;
    tabSwitches?: number;
    focusLossCount?: number;
    focusLosses?: number;
    fullscreenExitCount?: number;
    fullscreenExits?: number;
    pageVisibilityChanges?: number;
  };
  // Per-answer grading — primary source returned by the detail endpoint
  studentAnswers?: Array<{ isCorrect?: boolean; [key: string]: any }>;
  // Per-question grading detail — alternative field some endpoints return
  questionDetails?: Array<{
    questionId?: number;
    isCorrect?: boolean;
    points?: number;
    earnedPoints?: number;
  }>;
  // allow any extra keys the API may return
  [key: string]: any;
}

export function mapSession(raw: RawSessionDto, mapper: SessionResultMapperService): ProfessorStudentSessionDto {
  const firstName = raw.student?.firstName ?? '';
  const lastName  = raw.student?.lastName  ?? '';
  const fullName  = raw.studentFullName
    || (firstName || lastName ? `${firstName} ${lastName}`.trim() : '-');

  const subjectName =
    raw.subjectName
    || raw.subject?.name
    || raw.test?.subjectName
    || raw.test?.subject?.name
    || '-';

  const resolvedPercentage =
    raw.percentageScore != null ? raw.percentageScore
    : raw.percentage    != null ? raw.percentage
    : raw.score         != null ? raw.score
    : null;

  const counts   = mapper.computeAnswerCounts(raw);
  const cheating = mapper.extractCheatingStats(raw);

  return {
    id:                   raw.id,
    studentId:            raw.studentId ?? raw.student?.id ?? '',
    studentFullName:      fullName,
    studentEmail:         raw.studentEmail ?? raw.student?.email ?? '',
    testId:               raw.testId ?? raw.test?.id ?? 0,
    testTitle:            raw.testTitle ?? raw.test?.title ?? '-',
    testMode:             raw.testMode ?? raw.test?.testMode ?? raw.test?.mode ?? '',
    subjectId:            raw.test?.subjectId ?? raw.subject?.id ?? null,
    subjectName,
    learningPathId:       raw.test?.learningPathId ?? null,
    learningPathTitle:    raw.learningPathTitle ?? raw.test?.learningPathTitle ?? '',
    professorId:          raw.test?.professorId ?? null,
    professorName:        raw.test?.professorName ?? '',
    status:               raw.status ?? '',
    startedAt:            raw.startedAt ?? '',
    completedAt:          raw.completedAt ?? null,
    timeSpentSeconds:     raw.timeSpentSeconds ?? null,
    attemptNumber:        raw.attemptNumber ?? 0,
    score:                raw.score ?? null,
    percentage:        resolvedPercentage,
    percentageScore:   resolvedPercentage,
    passed:            raw.passed     ?? null,
    ...counts,
    totalPoints:       raw.totalPoints ?? null,
    earnedPoints:      raw.earnedPoints ?? null,
    ...cheating,
  };
}

export interface ProfessorStudentSessionDto {
  id: number;
  studentId: string;
  studentFullName: string;
  studentEmail: string;
  testId: number;
  testTitle: string;
  testMode: string;
  subjectId: number | null;
  subjectName: string;
  learningPathId: number | null;
  learningPathTitle: string;

  /** Auteur du test — c’est ce sens-là que filtre l’écran d’administration. */
  professorId: number | null;
  professorName: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  timeSpentSeconds: number | null;
  attemptNumber: number;
  score: number | null;
  percentage: number | null;
  percentageScore?: number | null;
  passed: boolean | null;
  correctAnswers: number | null;
  wrongAnswers: number | null;
  unansweredQuestions?: number | null;
  totalQuestions: number | null;
  totalPoints?: number | null;
  earnedPoints?: number | null;
  tabSwitchCount: number;
  focusLossCount: number;
  fullscreenExitCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProfessorService {
  private baseUrl = `${environment.BASE_URL}/professors`;
  private assignmentsSubject = new BehaviorSubject<ProfessorAssignment[]>([]);
  public assignments$ = this.assignmentsSubject.asObservable();
  private mapper = inject(SessionResultMapperService);

  constructor(
    private http: HttpClient,
    private errorHandler: HandleErrorsService,
  ) {}

  createProfessor(dto: CreateProfessorDto): Observable<ApiResponse<Professor>> {
    return this.http.post<ApiResponse<Professor>>(`${this.baseUrl}`, dto);
  }

  updateProfessor(
    professorId: number,
    dto: UpdateProfessorDto,
  ): Observable<ApiResponse<Professor>> {
    return this.http.put<ApiResponse<Professor>>(
      `${this.baseUrl}/${professorId}`,
      dto,
    );
  }

  /**
   * Provisionne le compte d'accès (Keycloak) d'un professeur qui n'en a pas encore.
   * Le back attribue le rôle « professor » et envoie un email de vérification
   * portant les actions VERIFY_EMAIL et UPDATE_PASSWORD.
   */
  createProfessorAccount(
    professorId: number,
  ): Observable<ApiSingleResponse<Professor>> {
    return this.http.post<ApiSingleResponse<Professor>>(
      `${this.baseUrl}/${professorId}/account`,
      {},
    );
  }

  deleteProfessor(professorId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.baseUrl}/${professorId}`,
    );
  }

  getById(professorId: string): Observable<ApiSingleResponse<Professor>> {
    return this.http.get<ApiSingleResponse<Professor>>(
      `${this.baseUrl}/${professorId}`,
    );
  }

  /**
   * Fiche professeur rattachée à un compte Keycloak, ou absence de fiche.
   *
   * <p>Cet endpoint n'exige pas le claim `id_professor` : c'est précisément ce
   * qui permet de l'utiliser pour détecter qu'un compte porte le rôle
   * « professor » sans fiche correspondante en base — le cas où tous les
   * autres appels répondraient 401 sans expliquer pourquoi.</p>
   */
  getByUserId(userId: string): Observable<ApiSingleResponse<Professor>> {
    return this.http.get<ApiSingleResponse<Professor>>(
      `${this.baseUrl}/by-user/${userId}`,
    );
  }

  /**
   * Toutes les affectations parcours·matière, tous professeurs confondus.
   * Seule source qui porte les libellés (matière + parcours) et qui s'interroge
   * par `professorId` : `getAssignmentsByProfessor` exige un GUID Keycloak, donc
   * inutilisable pour un professeur qui n'a pas encore de compte.
   */
  getAllAssignments(): Observable<ApiCollectionResponse<ProfessorAssignment>> {
    return this.http.get<ApiCollectionResponse<ProfessorAssignment>>(
      `${this.baseUrl}/assignments`,
    );
  }

  getAssignmentsByProfessor(
    professorId: string,
  ): Observable<ApiCollectionResponse<ProfessorAssignment>> {
    return this.http.get<ApiCollectionResponse<ProfessorAssignment>>(
      `${this.baseUrl}/by-user/${professorId}/assignment`,
    );
  }

  loadAssignments(professorId: string): void {
    this.getAssignmentsByProfessor(professorId).subscribe({
      next: (res) => {
        //console.log('📚 Assignments loaded:', res?.data);
        this.assignmentsSubject.next(res.data);
      },
      error: (err) => {
        this.errorHandler.handle('SWAL.FETCH_USER_ERROR');
      },
    });
  }

  // Optional: if you still want a snapshot version
  getProfessorAssignments(): ProfessorAssignment[] {
    return this.assignmentsSubject.getValue();
  }

  /**
   * Liste des professeurs. Sans `subjectId`, l'API les renvoie **tous** : c'est
   * ce qui permet à l'écran d'administration de filtrer par parcours (ou de ne
   * pas filtrer du tout) sans imposer le choix d'une matière.
   */
  getProfessors(subjectId?: number | null): Observable<ApiResponse<Professor[]>> {
    let params = new HttpParams();
    if (subjectId != null) {
      params = params.set('subjectId', subjectId.toString());
    }

    return this.http.get<ApiResponse<Professor[]>>(this.baseUrl, { params });
  }

  // ── Sessions étudiants ──────────────────────────────────────────────────

  private professorBaseUrl = `${environment.BASE_URL}/professor`;

  /** Toutes les sessions de la période académique active pour ce professeur */
  getActivePeriodSessions(): Observable<ApiCollectionResponse<ProfessorStudentSessionDto>> {
    return this.http.get<ApiCollectionResponse<RawSessionDto>>(
      `${this.professorBaseUrl}/tests/sessions/active-period`,
    ).pipe(
      map((res) => ({ ...res, data: (res.data ?? []).map((r) => mapSession(r, this.mapper)) })),
    );
  }

  /** Sessions d'un étudiant donné pour la période académique active */
  getActivePeriodSessionsByStudent(
    studentId: string,
  ): Observable<ApiCollectionResponse<ProfessorStudentSessionDto>> {
    return this.http.get<ApiCollectionResponse<RawSessionDto>>(
      `${this.professorBaseUrl}/tests/sessions/active-period/student/${studentId}`,
    ).pipe(
      map((res) => ({ ...res, data: (res.data ?? []).map((r) => mapSession(r, this.mapper)) })),
    );
  }

  /**
   * Fetches the full session detail from the student-tests endpoint.
   * This is the only endpoint that includes the cheating metadata
   * (tabSwitchCount, focusLossCount, fullscreenExitCount).
   * Returns a partial DTO to be merged onto the base session object.
   */
  getSessionFullDetail(sessionId: number): Observable<Partial<ProfessorStudentSessionDto>> {
    return this.http
      .get<ApiSingleResponse<RawSessionDto>>(
        `${environment.BASE_URL}/student-tests/sessions/${sessionId}`,
      )
      .pipe(
        map((res) => {
          const raw = res?.data;
          if (!raw) return {};

          const resolvedPercentage =
            raw.percentageScore != null ? raw.percentageScore
            : raw.percentage    != null ? raw.percentage
            : raw.score         != null ? raw.score
            : null;

          const counts  = this.mapper.computeAnswerCounts(raw);
          const cheating = this.mapper.extractCheatingStats(raw);

          return {
            percentageScore:     resolvedPercentage,
            percentage:          resolvedPercentage,
            score:               raw.score            ?? null,
            ...counts,
            earnedPoints:        raw.earnedPoints     ?? null,
            totalPoints:         raw.totalPoints      ?? null,
            timeSpentSeconds:    raw.timeSpentSeconds ?? null,
            passed:              raw.passed           ?? null,
            completedAt:         raw.completedAt      ?? null,
            ...cheating,
          } as Partial<ProfessorStudentSessionDto>;
        }),
        catchError((err) => {
          console.warn('[getSessionFullDetail] enrichment call failed, keeping list data:', err?.message);
          return of({} as Partial<ProfessorStudentSessionDto>);
        }),
      );
  }

}
